import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';

const logger = rootLogger.create('store/modules/questions');

/* eslint no-param-reassign: ["error", { "props": false }] */

function makeQuestion(question) {
  return {
    timeAsked: Date.now(),
    failures: 0,
    submitting: false,
    answers: [],
    ...question
  };
}

const mutations = {
  setSettings(state, payload) {
    state.settings = payload.settings;
  },

  setFetchingSettings(state, payload) {
    state.fetchingSettings = payload.fetchingSettings;
  },

  addQuestion(state, payload) {
    const question = makeQuestion(payload.question);
    state.all.push(question);
  },

  updateQuestion(state, payload) {
    for (let index = 0; index < state.all.length; index++) {
      const question = state.all[index];
      if (question.questionId === payload.question.questionId) {
        const updated = Object.assign({}, question, payload.question);
        state.all.splice(index, 1, updated);
        break;
      }
    }
  },

  addAnswer(state, payload) {
    let target = state.all.find(question => question.questionId === payload.questionId);
    if (!target) {
      // if we didn't ask the question, add the question and the answer
      const answer = payload.answer;
      const question = makeQuestion(answer);
      question.questionId = payload.questionId;
      question.text = question.questionText;
      state.all.push(question);
      target = question;
    }
    target.answers.push(payload.answer);
  },

  setAll(state, payload) {
    state.all = payload.all;
  },

  reset(state) {
    state.settings = null;
    state.fetchingSettings = false;
    state.all = [];
  }
};

const actions = {
  async fetchSettings({ commit, rootState, rootGetters }) {
    commit('setFetchingSettings', { fetchingSettings: true });

    const { eventKey } = rootState.g2wSession.joinInfo;
    let url = rootGetters['g2wEnv/questionServiceBaseUrl'];
    url += `/events/${eventKey}/settings/effective`;

    try {
      const settings = await fetcher.fetchJSON(url);
      commit('setFetchingSettings', { fetchingSettings: false });
      commit('setSettings', { settings });

      logger.log('fetched settings', { settings });
    } catch (error) {
      commit('setFetchingSettings', { fetchingSettings: false });
      logger.log('cannot fetch settings', {error});

      // ignore service error and enable Q&A
      const defaultSettings = {
        enableQandA: true,
        autoResponseText: ''
      };
      commit('setSettings', { settings: defaultSettings });
    }
  },

  async submit({
    commit, state, rootState, rootGetters
  }, payload) {
    const question = payload.question;
    const questionText = question.questionText;

    question.questionId = question.questionId || Date.now();

    if (rootState.previewMode) {
      logger.log('questionSubmission disabled in preview mode');
      commit('addQuestion', { question });

      commit('addAnswer', {
        questionId: question.questionId,
        answer: {
          answerText: state.settings ? state.settings.autoResponseText : '',
          public: false,
          time: Date.now()
        }
      });
      return question;
    }

    question.submitting = true;
    question.failures = question.failures || 0;
    if (!question.failures) {
      commit('addQuestion', {question});
    } else {
      commit('updateQuestion', {question});
    }

    const {
      webinarKey, eventKey, sessionKey, registrantKey
    } = rootState.g2wSession.joinInfo;
    let url = rootGetters['g2wEnv/questionServiceBaseUrl'];
    url += `/events/${eventKey || webinarKey}/sessions/${sessionKey}`;
    url += `/attendees/${registrantKey}/questions`;

    const body = {
      questionText,
      meta: {
        participantId: rootState.g2wSession.joinInfo.participantId
      }
    };

    const fetchParams = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(body)
    };

    const options = {
      operationName: 'submit'
    };

    try {
      const json = await fetcher.fetchJSON(url, fetchParams, options);
      question.submitting = false;

      if (question.failures) {
        // successful retry
        question.failures = 0;
        commit('updateQuestion', {question});
      }

      const answer = {
        answerText: json.autoResponseText,
        public: false,
        time: Date.now()
      };
      commit('addAnswer', {questionId: question.questionId, answer});
    } catch (error) {
      logger.log('cannot submit question', error);
      question.submitting = false;
      question.failures += 1;
      commit('updateQuestion', {question});
    }

    return question;
  },

  async reset({commit}) {
    commit('setSettings', {settings: null});
    commit('setFetchSettings', {fetchSettings: false});
    commit('setAll', {all: []});
  }
};

const getters = {
  enabled(state) {
    return !!state.settings && !!state.settings.enableQandA;
  }
};

const state = () => ({
  settings: null,
  fetchingSettings: false,
  all: []
});

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
